import Decimal from "decimal.js";
import $ from "jquery";
import { MapLoader, MapSize, MapStyle, MapView } from "./App/Basemap";
import { BasemapTiles } from "./App/BasemapTiles";
import { Contour } from "./App/Contour";
import {
    ForecastTime,
    InitialTime,
    ShadeSelector,
    shadeVariables,
} from "./App/Controller";
import { Downloader } from "./App/Downloader";
import {
    ImageZipExporter,
    type ImageZipRequest,
    type ImageZipResult,
} from "./App/ImageZipExporter";
import type { MapModel } from "./App/MapMeta";
import { Clock, Credit } from "./App/Overlay";
import { Particle } from "./App/Particle";
import { PointArrows } from "./App/PointArrows";
import { Points } from "./App/Points";
import { RenderCoordinator, type RenderStatus } from "./App/RenderCoordinator";
import { Shade, ShadeLegend } from "./App/Shade";
import {
    VideoRecorder,
    type VideoRequest,
    type VideoResult,
} from "./App/VideoRecorder";
import { Wind } from "./App/Wind";

export class App {
    static async fetch() {
        const search = new URLSearchParams(window.location.search);
        MapLoader.tilesMode = BasemapTiles.isEnabled(search);
        const requestedElement = search.get("el_elm");
        const initialMapModel = shadeVariables.includes(
            requestedElement as (typeof shadeVariables)[number],
        )
            ? (requestedElement!.slice(0, 3) as MapModel)
            : "MSM";
        // 下地タイルの取得 (header/metadata/cliparea/初期画角タイル) をここで開始する。
        // await はしない — init() の activate()/onViewChanged() が同じ取得を待つので、
        // latest.json 等と並走させて初回描画の直列往復を前倒しするだけでよい。
        BasemapTiles.prefetch(search, initialMapModel);
        await Promise.all([
            MapLoader.fetch(initialMapModel),
            InitialTime.fetch(),
            Clock.fetch(),
            Points.fetch(),
        ]);
    }
    private readonly params: URLSearchParams;
    private readonly mapLoader: MapLoader;
    private readonly basemapTiles: BasemapTiles | null;
    private readonly mapView: MapView;
    private readonly mapStyle: MapStyle;
    private readonly shadeSelector: ShadeSelector;
    private readonly initialTime: InitialTime;
    private readonly forecastTime: ForecastTime;
    private readonly clock: Clock;
    private readonly credit: Credit;
    private readonly points: Points;
    private readonly pointArrows: PointArrows;
    private readonly shade: Shade;
    private readonly shadeLegend: ShadeLegend;
    private readonly contour: Contour;
    private readonly wind: Wind;
    private readonly particle: Particle;
    private readonly downloader: Downloader;
    private readonly videoRecorder: VideoRecorder;
    private readonly imageZipExporter: ImageZipExporter;
    readonly render = new RenderCoordinator();
    constructor() {
        this.params = new URLSearchParams(window.location.search);
        this.migrateLegacyParams();

        new MapSize();
        this.shadeSelector = new ShadeSelector(this.params);
        const initialModel = this.shadeSelector.getParams().model;
        this.mapLoader = new MapLoader(initialModel);
        this.mapView = new MapView(this.params, initialModel);
        this.basemapTiles = BasemapTiles.isEnabled(this.params)
            ? new BasemapTiles(this.mapView)
            : null;
        this.mapStyle = new MapStyle(this.params, this.mapView);

        this.mapStyle.setShadeVariableGetter(
            () => this.shadeSelector.getParams().shadeVariable,
        );
        this.initialTime = new InitialTime(this.params, this.shadeSelector);
        this.forecastTime = new ForecastTime(
            this.params,
            this.shadeSelector,
            this.initialTime,
        );

        this.clock = new Clock(
            this.params,
            this.shadeSelector,
            this.initialTime,
            this.forecastTime,
        );
        this.credit = new Credit(this.params);

        this.points = new Points(this.params, this.mapView);
        this.pointArrows = new PointArrows(
            this.params,
            this.shadeSelector,
            this.initialTime,
            this.forecastTime,
            this.mapView,
        );

        this.shade = new Shade(
            this.params,
            this.shadeSelector,
            this.initialTime,
            this.forecastTime,
            this.mapView,
        );
        this.shadeLegend = new ShadeLegend(this.params, this.shadeSelector);

        this.contour = new Contour(
            this.params,
            this.shadeSelector,
            this.initialTime,
            this.forecastTime,
            this.mapView,
        );
        this.contour.onChange((task) => this.runRender("contour", task));

        this.wind = new Wind(
            this.params,
            this.shadeSelector,
            this.initialTime,
            this.forecastTime,
            this.mapView,
            () => this.shade.getDisplayLevel(),
        );

        this.particle = new Particle(
            this.params,
            this.shadeSelector,
            this.initialTime,
            this.forecastTime,
            this.mapView,
            async (ft) => {
                this.forecastTime.set(new Decimal(ft));
                await this.onParticleFrameChanged();
            },
        );
        this.particle.onChange((task) => this.runRender("particle", task));

        this.downloader = new Downloader(
            this.shade,
            this.wind,
            this.contour,
            this.params,
        );
        const frameHost = {
            renderFrame: async (ft: Decimal) => {
                this.forecastTime.set(ft);
                await this.onForecastTimeChanged();
            },
            prepareFrames: (fts: Decimal[]) =>
                this.clock.prepareForecastTimes(fts),
            getForecastTime: () => this.forecastTime.getParams().ft,
            getRange: () => this.forecastTime.getRangeParams(),
            waitIdle: () => this.render.wait(),
            getQuery: () => {
                this.saveParams(true);
                return this.params.toString();
            },
            isVideoAvailable: () =>
                !["GSMV", "MSMV"].includes(
                    this.shadeSelector.getParams().shadeVariable,
                ),
        };
        this.videoRecorder = new VideoRecorder(
            frameHost,
            this.downloader,
            this.render,
        );
        this.imageZipExporter = new ImageZipExporter(
            frameHost,
            this.downloader,
            this.render,
        );
        // 「画像生成」: 時間幅0なら静止画1枚 (完成時に別タブのビューアで開く)、
        // 1時間以上なら現在FTからの複数枚を従来どおりZIPでDLする
        $("#download_image").on("click", () => {
            const hours = Number($("#input_topngs_hour").val());
            if (Number.isFinite(hours) && hours > 0) {
                this.imageZipExporter.downloadZipFromUi();
            } else {
                this.downloader.downloadImageFromUi();
            }
        });

        this.mapView.onZoom(() => this.runRender("app", this.onZoomed()));
        this.mapView.onDrag(() => this.runRender("app", this.onDragged()));
        this.mapView.onRotate(() => this.runRender("app", this.onRotated()));

        this.shadeSelector.onChange(() =>
            this.runRender("app", this.onShadeVariableChanged()),
        );
        this.shade.onChange((task) => this.runRender("shade", task));
        this.wind.onChange((task) => this.runRender("wind", task));
        this.pointArrows.onChange((task) => this.runRender("arrow", task));
        this.initialTime.onChange(() =>
            this.runRender("app", this.onInitialTimeChanged()),
        );
        this.forecastTime.onChange(() =>
            this.runRender("app", this.onForecastTimeChanged()),
        );

        for (const preset of [
            "light",
            "darkwind",
            "thunder",
            "trans",
            "japan",
            "honshu",
            "okinawa-kanto",
            "kyushu-kanto",
            "kanto",
            "south",
            "north",
            "far-east",
            "nh1",
            "nh2",
            "dust",
        ]) {
            $(`#preset_${preset}`).on("click", () => this.usePreset(preset));
        }

        // URL発行はNOWC250と同様にボタン1つ+時刻checkbox
        $("#write_url").on("click", () =>
            this.writeURL($("#cb_urlTime").prop("checked") === true),
        );
        $("#clear_url").on("click", () => this.clearURL());
    }
    async init() {
        // 下地地図のactivate待ちより前に現在FTのPMTiles取得を開始する。
        // 実composeはrefresh()のままなので、描画完了契約は変えない。
        void this.shade.prefetchCurrent().catch(() => undefined);
        if (this.basemapTiles) {
            // coordinator管理下で読み込む。trackを同期的に呼びpendingへ入れる
            // ことで、init開始直後から renderReady=false になる。未管理だと
            // basemap挿入前の一瞬 pending が空になり、renderReady を待つ
            // 動画生成 (CF worker・API) が地図なしのフレームを取り込む
            const basemapTiles = this.basemapTiles;
            await this.render.track(
                "map",
                (async () => {
                    await basemapTiles.activate();
                    await basemapTiles.onViewChanged();
                })(),
            );
        }
        await this.refresh();
        const ready = await this.render.wait();
        this.clock.preloadRemainingWhenIdle();
        return ready;
    }
    async setParams(inputParams: Record<string, string>) {
        this.clearParams();
        this.mergeParams(inputParams);
        await this.applyParams();
        return this.render.wait();
    }
    async changeParams(inputParams: Record<string, string>) {
        this.saveParams(true);
        this.mergeParams(inputParams);
        await this.applyParams();
        return this.render.wait();
    }
    async toDataURL() {
        await this.render.wait();
        return this.render.track("export", this.downloader.toDataURL());
    }
    renderVideo(request: VideoRequest): Promise<VideoResult> {
        this.particle.stopPlayback(); // 再生ループと FT 駆動の競合を防ぐ
        return this.videoRecorder.record(request);
    }
    abortVideo() {
        this.videoRecorder.abort();
    }
    renderImageZip(request: ImageZipRequest): Promise<ImageZipResult> {
        this.particle.stopPlayback();
        return this.imageZipExporter.create(request);
    }
    abortImageZip() {
        this.imageZipExporter.abort();
    }
    waitForRender(timeoutMs?: number) {
        return this.render.wait(timeoutMs);
    }
    getRenderStatus(): RenderStatus {
        return this.render.status();
    }
    private saveParams(saveTime: boolean) {
        const keys = [...this.params.keys()];
        for (const key of keys) {
            this.params.delete(key);
        }
        this.shadeSelector.saveParams();
        if (saveTime) {
            this.initialTime.saveParams();
        }
        // 相対時刻URLも、最新初期値に対する同じFTを再現する。
        this.forecastTime.saveParams();
        this.mapView.saveParams();
        this.mapStyle.saveParams();
        this.clock.saveParams();
        this.credit.saveParams();
        this.points.saveParams();
        this.pointArrows.saveParams();
        this.shade.saveParams();
        this.shadeLegend.saveParams();
        this.contour.saveParams();
        this.wind.saveParams();
        this.particle.saveParams();
        this.downloader.saveParams();
    }
    private onZoomed() {
        this.mapStyle.updateWidth();
        this.points.updateSize();
        this.contour.updateWidth();
        return Promise.all([
            this.shade.updateExtent(),
            this.wind.onViewChanged(),
            this.pointArrows.onViewChanged(),
            this.particle.onViewChanged(),
            this.basemapTiles?.onViewChanged(),
        ]);
    }
    private onDragged() {
        return Promise.all([
            this.shade.updateExtent(),
            this.wind.onViewChanged(),
            this.pointArrows.onViewChanged(),
            this.particle.onViewChanged(),
            // 等値線キャンバスは現画角ぶんしか描いていないため、ドラッグで露出した領域の再描画が必要
            this.contour.onViewChanged(),
            this.basemapTiles?.onViewChanged(),
        ]);
    }
    private onRotated() {
        this.points.updateSize();
        return Promise.all([
            this.shade.updateExtent(),
            this.wind.onViewChanged(),
            this.pointArrows.onViewChanged(),
            this.particle.onViewChanged(),
            this.contour.onViewChanged(),
            this.basemapTiles?.onViewChanged(),
        ]);
    }
    private async onShadeVariableChanged() {
        this.videoRecorder.onShadeVariableChanged();
        const { model } = this.shadeSelector.getParams();
        await this.mapLoader.setModel(model);
        const mapChanged = this.mapView.setModel(model, true);
        await this.basemapTiles?.onViewChanged();
        this.initialTime.init();
        this.forecastTime.init();
        const clockUpdate = this.clock.update();
        this.mapStyle.onShadeVariableChanged();
        if (mapChanged) {
            this.mapStyle.updateWidth();
            this.points.updateSize();
        }
        const shadeUpdate = this.shade.onShadeVariableChanged();
        this.shadeLegend.update();
        const contourUpdate = this.contour.onShadeVariableChanged();
        const windUpdate = this.wind.onShadeVariableChanged();
        const arrowUpdate = this.pointArrows.onShadeVariableChanged();
        const particleUpdate = this.particle.onVariableChanged();
        return Promise.all([
            shadeUpdate,
            clockUpdate,
            contourUpdate,
            windUpdate,
            arrowUpdate,
            particleUpdate,
        ]);
    }
    private onInitialTimeChanged() {
        this.forecastTime.onInitialTimeChanged();
        const clockUpdate = this.clock.update();
        const shadeUpdate = this.shade.onInitialTimeChanged();
        const contourUpdate = this.contour.onInitialTimeChanged();
        const windUpdate = this.wind.onInitialTimeChanged();
        const arrowUpdate = this.pointArrows.onInitialTimeChanged();
        const particleUpdate = this.particle.onInitialTimeChanged();
        return Promise.all([
            shadeUpdate,
            clockUpdate,
            contourUpdate,
            windUpdate,
            arrowUpdate,
            particleUpdate,
        ]);
    }
    private onForecastTimeChanged() {
        const clockUpdate = this.clock.update();
        const shadeUpdate = this.shade.onForecastTimeChanged();
        const contourUpdate = this.contour.onForecastTimeChanged();
        const windUpdate = this.wind.onForecastTimeChanged();
        const arrowUpdate = this.pointArrows.onForecastTimeChanged();
        const particleUpdate = this.particle.onForecastTimeChanged();
        return Promise.all([
            shadeUpdate,
            clockUpdate,
            contourUpdate,
            windUpdate,
            arrowUpdate,
            particleUpdate,
        ]);
    }
    private onParticleFrameChanged() {
        const clockUpdate = this.clock.update();
        return Promise.all([
            clockUpdate,
            this.shade.onForecastTimeChanged(),
            this.contour.onForecastTimeChanged(),
            this.wind.onForecastTimeChanged(),
            this.pointArrows.onForecastTimeChanged(),
        ]);
    }
    private usePreset(preset: string) {
        if (!this.mapView.usePreset(preset)) {
            return;
        }
        this.mapStyle.usePreset(preset);
        this.clock.usePreset(preset);
        this.shadeLegend.usePreset(preset);
        this.runRender("app", this.onZoomed());
    }
    private writeURL(saveTime: boolean) {
        this.saveParams(saveTime);
        const url = new URL(window.location.href);
        url.search = this.params.toString();
        history.replaceState(null, "", url);
    }
    private clearURL() {
        const url = new URL(window.location.href);
        url.search = "";
        history.replaceState(null, "", url);
    }
    private async refresh() {
        const clock = this.clock.update();
        const shade = this.shade.onForecastTimeChanged();
        const contour = this.contour.onForecastTimeChanged();
        const wind = this.wind.onForecastTimeChanged();
        const arrow = this.pointArrows.onShadeVariableChanged();
        const particle = this.particle.refresh();
        await Promise.all([
            this.render.track("clock", clock),
            this.render.track("shade", shade),
            this.render.track("contour", contour),
            this.render.track("wind", wind),
            this.render.track("arrow", arrow),
            this.render.track("particle", particle),
        ]);
    }
    private async applyParams() {
        this.shadeSelector.loadParams();
        this.videoRecorder.onShadeVariableChanged();
        const { model } = this.shadeSelector.getParams();
        await this.mapLoader.setModel(model);
        this.mapView.setModel(model, false);
        await this.basemapTiles?.onViewChanged();
        this.initialTime.init();
        this.initialTime.loadParams();
        this.forecastTime.loadParams();
        this.mapView.loadParams();
        this.mapStyle.loadParams();
        const clock = this.clock.applyParams();
        this.points.loadParams();
        this.shade.loadParams();
        this.downloader.loadParams();
        this.contour.loadParams();
        this.wind.loadParams();
        this.pointArrows.loadParams();
        this.particle.loadParams();
        await Promise.all([
            this.render.track("clock", clock),
            this.credit.applyParams(),
            this.shadeLegend.applyParams(),
            this.render.track("shade", this.applyShadeParams()),
            this.render.track("contour", this.contour.onShadeVariableChanged()),
            this.render.track("wind", this.wind.onShadeVariableChanged()),
            this.render.track(
                "arrow",
                this.pointArrows.onShadeVariableChanged(),
            ),
            this.render.track("particle", this.particle.onVariableChanged()),
        ]);
    }
    private clearParams() {
        for (const key of [...this.params.keys()]) {
            this.params.delete(key);
        }
    }
    private mergeParams(inputParams: Record<string, string>) {
        for (const [key, value] of Object.entries(inputParams)) {
            this.params.set(key, value);
        }
        this.migrateLegacyParams(new Set(Object.keys(inputParams)));
    }
    private migrateLegacyParams(changedKeys = new Set<string>()) {
        const alias = (legacy: string, current: string, value?: string) => {
            if (!this.params.has(legacy)) {
                return;
            }
            if (!this.params.has(current) || changedKeys.has(legacy)) {
                this.params.set(current, value ?? this.params.get(legacy)!);
            }
        };
        alias("prp_ini", "ini");
        alias("sty_landJC", "sty_landJ");
        alias("sty_coastJC", "sty_coastJ");
        for (const [legacy, current] of [
            ["wd_el", "wd_on"],
            ["con_el", "con_on"],
            ["ar_el", "ar_on"],
        ]) {
            if (this.params.has(legacy)) {
                alias(
                    legacy,
                    current,
                    this.params.get(legacy)?.trim() ? "1" : "0",
                );
            }
        }

        const legacyStyle = ["prp_ini", "sty_landJC", "sty_coastJC"].some(
            (key) => this.params.has(key),
        );
        if (legacyStyle) {
            for (const key of [
                "sty_backC",
                "sty_landJ",
                "sty_coastJ",
                "sty_prefC",
                "sty_landFC",
                "sty_coastFC",
                "sty_lonlatC",
            ]) {
                const value = this.params.get(key);
                if (value && /^#[0-9a-f]{8}$/i.test(value)) {
                    this.params.set(
                        key,
                        `#${value.slice(3)}${value.slice(1, 3)}`,
                    );
                }
            }
        }
    }
    private async applyShadeParams() {
        await this.shade.onShadeVariableChanged();
        await this.shade.updateExtent();
    }
    private runRender(
        part: "app" | "shade" | "contour" | "wind" | "arrow" | "particle",
        task: Promise<unknown>,
    ) {
        this.render.track(part, task).catch((error) => console.error(error));
    }
}
